'use client';

import { useState, useEffect } from 'react';
import { submitContactForm, submitNewsletterForm } from '@/app/actions';

export function NewsletterForm({ 
  domain, 
  currentPath = '/' 
}: { 
  domain: string; 
  currentPath?: string; 
}) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const formData = new FormData(e.currentTarget);
    const result = await submitNewsletterForm(formData);

    if (result.success) {
      setStatus('success');
    } else {
      setStatus('error');
      setErrorMessage(result.error || 'Ocurrió un error.');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-lg text-center animate-in fade-in">
        <span className="text-emerald-800 text-xs font-bold block">✓ ¡Suscripción confirmada!</span>
        <span className="text-emerald-600 text-[11px] block mt-1">Recibirás los boletines técnicos de {domain}.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <input type="hidden" name="domain" value={domain} />
      <input type="hidden" name="url" value={`https://${domain}${currentPath}`} />

      {/* Honeypot field (hidden from legitimate users, tempting for scrapers) */}
      <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
        <input 
          type="text" 
          name="newsletter_subscriber_token" 
          tabIndex={-1} 
          autoComplete="off" 
        />
      </div>

      <div className="flex gap-2">
        <input 
          type="email" 
          name="email" 
          placeholder="correo@corporativo.com" 
          required 
          className="flex-1 p-2.5 text-xs rounded border border-slate-300 text-slate-900 outline-none focus:border-slate-900" 
        />
        <button 
          type="submit" 
          disabled={status === 'loading'}
          className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded text-xs font-bold transition disabled:opacity-50"
        >
          {status === 'loading' ? 'Enviando...' : 'Suscribir'}
        </button>
      </div>
      {status === 'error' && (
        <span className="text-[11px] text-red-600 font-semibold">{errorMessage}</span>
      )}
    </form>
  );
}

export function ContactForm({ 
  domain, 
  currentPath = '/' 
}: { 
  domain: string; 
  currentPath?: string; 
}) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [mountTime, setMountTime] = useState<number>(0);

  useEffect(() => {
    setMountTime(Date.now());
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const formData = new FormData(e.currentTarget);
    const result = await submitContactForm(formData);

    if (result.success) {
      setStatus('success');
    } else {
      setStatus('error');
      setErrorMessage(result.error || 'Ocurrió un error inesperado.');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 bg-emerald-50 border-2 border-emerald-400 rounded-xl text-center shadow-sm animate-in fade-in">
        <div className="w-14 h-14 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-black shadow-md">
          ✓
        </div>
        <h4 className="text-emerald-950 font-black text-xl mb-2">¡Consulta Recibida con Éxito!</h4>
        <p className="text-emerald-800 text-sm leading-relaxed max-w-sm mx-auto mb-6">
          Hemos registrado tu solicitud para <strong>{domain}</strong>. Nuestro equipo técnico validará los datos y se pondrá en contacto a la brevedad.
        </p>
        <button 
          type="button" 
          onClick={() => setStatus('idle')} 
          className="text-xs font-bold text-emerald-700 underline hover:text-emerald-900 transition"
        >
          ← Enviar otra consulta
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <input type="hidden" name="domain" value={domain} />
      <input type="hidden" name="url" value={`https://${domain}${currentPath}`} />
      
      {/* Time trap: records when the form was rendered in client browser */}
      <input type="hidden" name="_form_timestamp" value={mountTime.toString()} />

      {/* Honeypot field (hidden from legitimate users, tempting for scrapers) */}
      <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
        <label htmlFor="corporate_fax_validation">Do not fill this field</label>
        <input 
          id="corporate_fax_validation"
          type="text" 
          name="corporate_fax_validation" 
          tabIndex={-1} 
          autoComplete="off" 
        />
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Nombre Completo</label>
        <input 
          type="text" 
          name="nombre" 
          placeholder="Ingresa tu nombre" 
          required 
          className="w-full p-3 border border-slate-300 rounded text-sm text-slate-900 outline-none focus:border-slate-900" 
        />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Correo Electrónico</label>
        <input 
          type="email" 
          name="email" 
          placeholder="correo@empresa.com" 
          required 
          className="w-full p-3 border border-slate-300 rounded text-sm text-slate-900 outline-none focus:border-slate-900" 
        />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Mensaje o Consulta</label>
        <textarea 
          name="mensaje" 
          placeholder="Describe brevemente tus requerimientos o dudas operativas..." 
          rows={4} 
          required 
          className="w-full p-3 border border-slate-300 rounded text-sm text-slate-900 outline-none focus:border-slate-900"
        ></textarea>
      </div>

      {status === 'error' && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
          {errorMessage}
        </div>
      )}

      <button 
        type="submit" 
        disabled={status === 'loading'}
        className="w-full bg-slate-950 hover:bg-slate-800 text-white font-bold py-3.5 rounded text-sm transition shadow-md disabled:opacity-50"
      >
        {status === 'loading' ? 'Procesando Envío...' : 'Enviar Consulta ↗'}
      </button>
    </form>
  );
}
